import { sb } from '../supabase.ts';
import { navigateTo } from '../router.ts';

/* ─────────────────── TYPES ─────────────────── */
type CellType = 'desk' | 'aisle' | 'broken' | 'instructor';

interface Room {
  id: string;
  building: string;
  room_no: string;
  rows: number;
  cols: number;
  col_rows: number[];
  bench_size: number;
  cellTypes?: Record<string, CellType>; // "row-col" → type
}

interface Student {
  iid: string;
  name: string;
  nameBn: string;
  father: string;
  cls: string;
  section: string;
  roll: string;
  group: string;
  gender: string;
}

interface SeatEntry {
  row: number;
  col: number;
  students: (Student | null)[];
}

interface PlanRoom {
  room_id: string;
  room_info: Room;
  seats: SeatEntry[];
}

interface Plan {
  id: string;
  exam: string;
  year: string;
  session: string;
  pattern: string;
  antiCheat: boolean;
  genderSep: boolean;
  created: string;
  rooms: PlanRoom[];
  unassigned: Student[];
}

interface FixedRule {
  id: string;
  classSection: string;
  rollRange: string;
  roomId: string;
  roomLabel: string;
  startRow: number;
  startCol: number;
}

interface AppState {
  rooms: Room[];
  students: Student[];
  currentPlan: Plan | null;
  savedPlans: Plan[];
  classOptions: { cls: string; section: string }[];
  classMap: Map<string, Set<string>>;
  sectionMap: Map<string, Set<string>>;
}

/* ─────────────────── CSS ─────────────────── */
const SEAT_PLAN_CSS = `
/* ── Variables ─────────────────────────────── */
.spp-root {
  --spp-bg: #f0f2f5; --spp-card: #ffffff; --spp-border: #e2e8f0; --spp-muted: #64748b;
  --spp-primary: #2563eb; --spp-primary-light: #dbeafe; --spp-primary-dark: #1d4ed8;
  --spp-success: #16a34a; --spp-success-light: #dcfce7;
  --spp-danger: #dc2626; --spp-danger-light: #fee2e2;
  --spp-warning: #d97706; --spp-warning-light: #fef3c7;
  --spp-radius: 10px; --spp-shadow: 0 1px 3px rgba(0,0,0,.08);
  --spp-font-bn: 'Kalpurush', 'Noto Sans Bengali', 'Hind Siliguri', system-ui, sans-serif;
  font-family: var(--spp-font-bn);
  background: var(--spp-bg);
  color: #1e293b;
  line-height: 1.5;
  min-height: 100vh;
}
.spp-root *, .spp-root *::before, .spp-root *::after { box-sizing: border-box; }

/* ── Layout ────────────────────────────────── */
.spp-header { background: var(--spp-card); border-bottom: 1px solid var(--spp-border); position: sticky; top: 0; z-index: 50; }
.spp-header-inner { max-width: 1400px; margin: 0 auto; padding: .75rem 1rem; display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
.spp-header h1 { font-size: 1.25rem; white-space: nowrap; margin: 0; }
.spp-header h1 span { color: var(--spp-primary); }
.spp-back-btn { display: inline-flex; align-items: center; gap: .35rem; padding: .4rem .85rem; border: 1px solid var(--spp-border); border-radius: 8px; background: var(--spp-card); font-family: inherit; font-size: .85rem; font-weight: 600; cursor: pointer; transition: all .15s; color: var(--spp-muted); text-decoration: none; }
.spp-back-btn:hover { border-color: var(--spp-primary); color: var(--spp-primary); background: var(--spp-primary-light); }
.spp-tabs { display: flex; gap: 2px; background: #f1f5f9; border-radius: var(--spp-radius); padding: 3px; margin-left: auto; }
.spp-tab-btn { padding: .45rem 1rem; border: none; background: transparent; border-radius: 8px; cursor: pointer; font-family: inherit; font-size: .9rem; font-weight: 600; color: var(--spp-muted); transition: all .15s; }
.spp-tab-btn.active { background: var(--spp-card); color: var(--spp-primary); box-shadow: var(--spp-shadow); }
.spp-tab-btn:hover:not(.active) { color: #334155; }
.spp-container { max-width: 1400px; margin: 0 auto; padding: 1rem; }
.spp-tab-panel { display: none; }
.spp-tab-panel.active { display: block; }
.spp-status-bar { font-size: .85rem; color: var(--spp-muted); padding: .25rem 0; }

/* ── Cards & Inputs ────────────────────────── */
.spp-card { background: var(--spp-card); border: 1px solid var(--spp-border); border-radius: var(--spp-radius); padding: 1.25rem; box-shadow: var(--spp-shadow); }
.spp-card-title { font-size: 1rem; font-weight: 700; margin-bottom: .75rem; display: flex; align-items: center; gap: .5rem; }
.spp-card-title .spp-icon { font-size: 1.2rem; }
.spp-root label { font-weight: 600; font-size: .85rem; display: block; margin-bottom: .25rem; }
.spp-root input, .spp-root select, .spp-root textarea { width: 100%; padding: .5rem .65rem; border: 1px solid var(--spp-border); border-radius: 8px; font-family: inherit; font-size: .9rem; background: #fff; }
.spp-root input:focus, .spp-root select:focus { outline: none; border-color: var(--spp-primary); box-shadow: 0 0 0 3px var(--spp-primary-light); }
.spp-form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: .75rem; margin-bottom: .75rem; }
.spp-btn { display: inline-flex; align-items: center; justify-content: center; gap: .4rem; padding: .5rem 1rem; border: 1px solid var(--spp-border); border-radius: 8px; background: var(--spp-card); font-family: inherit; font-size: .875rem; font-weight: 600; cursor: pointer; transition: all .15s; white-space: nowrap; }
.spp-btn:hover { border-color: #cbd5e1; background: #f8fafc; }
.spp-btn-primary { background: var(--spp-primary); border-color: var(--spp-primary-dark); color: #fff; }
.spp-btn-primary:hover { background: var(--spp-primary-dark); }
.spp-btn-success { background: var(--spp-success); border-color: #15803d; color: #fff; }
.spp-btn-danger { background: var(--spp-danger); border-color: #b91c1c; color: #fff; }
.spp-btn-sm { padding: .3rem .65rem; font-size: .8rem; }
.spp-btn-group { display: flex; gap: .5rem; flex-wrap: wrap; }

/* ── Dashboard ─────────────────────────────── */
.spp-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
.spp-stat-card { padding: 1.25rem; border-radius: var(--spp-radius); border: 1px solid var(--spp-border); background: var(--spp-card); }
.spp-stat-card .spp-number { font-size: 2rem; font-weight: 800; line-height: 1; }
.spp-stat-card .spp-label { font-size: .85rem; color: var(--spp-muted); margin-top: .25rem; }
.spp-stat-card.blue { border-left: 4px solid var(--spp-primary); }
.spp-stat-card.green { border-left: 4px solid var(--spp-success); }
.spp-stat-card.orange { border-left: 4px solid var(--spp-warning); }
.spp-stat-card.red { border-left: 4px solid var(--spp-danger); }

/* ── Room Setup ────────────────────────────── */
.spp-room-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem; margin-top: 1rem; }
.spp-room-card { position: relative; }
.spp-room-card .spp-room-meta { font-size: .85rem; color: var(--spp-muted); }
.spp-room-preview { display: grid; gap: 2px; margin: .75rem 0; padding: .5rem; background: #f8fafc; border-radius: 8px; }
.spp-room-preview .spp-seat-dot { width: 100%; aspect-ratio: 1; background: var(--spp-primary-light); border-radius: 3px; border: 1px solid #bfdbfe; cursor: pointer; transition: all .15s; display: flex; align-items: center; justify-content: center; font-size: .6rem; }
.spp-room-preview .spp-seat-dot:hover { transform: scale(1.15); box-shadow: 0 0 0 2px var(--spp-primary); }
.spp-room-preview .spp-seat-dot.aisle { background: #e2e8f0; border-color: #cbd5e1; background-image: repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,.06) 3px, rgba(0,0,0,.06) 6px); }
.spp-room-preview .spp-seat-dot.broken { background: #fee2e2; border-color: #fca5a5; }
.spp-room-preview .spp-seat-dot.instructor { background: #dcfce7; border-color: #86efac; }
.spp-room-actions { display: flex; gap: .5rem; }

/* ── Interactive Designer Brush ────────────── */
.spp-brush-panel { margin-top: 1rem; padding: 1rem; background: linear-gradient(135deg, #f8fafc, #f1f5f9); border-radius: var(--spp-radius); border: 1px solid var(--spp-border); }
.spp-brush-panel h4 { margin: 0 0 .25rem; font-size: .95rem; font-weight: 700; display: flex; align-items: center; gap: .5rem; }
.spp-brush-panel p { margin: 0 0 .75rem; font-size: .8rem; color: var(--spp-muted); }
.spp-brush-options { display: flex; flex-direction: column; gap: .5rem; }
.spp-brush-option { display: flex; align-items: center; gap: .75rem; padding: .65rem .85rem; border: 2px solid var(--spp-border); border-radius: 10px; cursor: pointer; transition: all .2s; background: #fff; }
.spp-brush-option:hover { border-color: #94a3b8; transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,.06); }
.spp-brush-option.active { border-color: var(--spp-primary); background: var(--spp-primary-light); box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.spp-brush-option input[type=radio] { display: none; }
.spp-brush-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0; }
.spp-brush-icon.desk { background: var(--spp-primary-light); border: 1px solid #93c5fd; }
.spp-brush-icon.aisle { background: #e2e8f0; border: 1px solid #cbd5e1; }
.spp-brush-icon.broken { background: #fee2e2; border: 1px solid #fca5a5; }
.spp-brush-icon.instructor { background: #dcfce7; border: 1px solid #86efac; }
.spp-brush-text { display: flex; flex-direction: column; }
.spp-brush-text strong { font-size: .85rem; }
.spp-brush-text span { font-size: .75rem; color: var(--spp-muted); }

/* ── Planner ───────────────────────────────── */
.spp-planner-layout { display: grid; grid-template-columns: 320px 1fr; gap: 1rem; align-items: start; }
@media (max-width: 900px) { .spp-planner-layout { grid-template-columns: 1fr; } }
.spp-config-panel { position: sticky; top: 80px; }
.spp-class-list { max-height: 200px; overflow-y: auto; border: 1px solid var(--spp-border); border-radius: 8px; padding: .5rem; }
.spp-class-item { display: flex; align-items: center; gap: .5rem; padding: .3rem .25rem; border-radius: 4px; }
.spp-class-item:hover { background: #f8fafc; }
.spp-class-item input[type=checkbox] { width: auto; }
.spp-room-check-list { max-height: 150px; overflow-y: auto; border: 1px solid var(--spp-border); border-radius: 8px; padding: .5rem; }
.spp-option-row { display: flex; align-items: center; gap: .75rem; padding: .4rem 0; }
.spp-option-row input[type=checkbox], .spp-option-row input[type=radio] { width: auto; }

/* ── Seat Grid ─────────────────────────────── */
.spp-grid-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: .5rem; margin-bottom: .75rem; }
.spp-room-tabs { display: flex; gap: 2px; background: #f1f5f9; border-radius: 8px; padding: 2px; flex-wrap: wrap; }
.spp-room-tab { padding: .35rem .75rem; border: none; background: transparent; border-radius: 6px; cursor: pointer; font-family: inherit; font-size: .8rem; font-weight: 600; color: var(--spp-muted); }
.spp-room-tab.active { background: var(--spp-card); color: var(--spp-primary); box-shadow: var(--spp-shadow); }
.spp-seat-grid-wrap { overflow-x: auto; }
.spp-seat-grid { display: grid; gap: 6px; padding: .5rem; min-width: fit-content; }
.spp-seat-cell { position: relative; min-width: 110px; min-height: 72px; padding: .4rem .5rem; border: 2px solid var(--spp-border); border-radius: 8px; background: #f8fafc; cursor: grab; transition: all .15s; display: flex; flex-direction: column; justify-content: flex-start; gap: 2px; }
.spp-seat-cell .bench-student { padding: 2px 0; border-bottom: 1px dashed var(--spp-border); }
.spp-seat-cell .bench-student:last-child { border-bottom: none; }
.spp-capacity-warning { padding: .75rem 1rem; background: var(--spp-warning-light); border: 1px solid var(--spp-warning); border-radius: var(--spp-radius); color: #92400e; font-weight: 600; margin-bottom: .75rem; }
.spp-seat-cell.occupied { border-color: #93c5fd; background: #eff6ff; cursor: grab; }
.spp-seat-cell.empty { border-style: dashed; opacity: .6; cursor: default; }
.spp-seat-cell.cell-aisle { border-style: dashed; opacity: .5; cursor: default; background: #e2e8f0; background-image: repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,.04) 3px, rgba(0,0,0,.04) 6px); }
.spp-seat-cell.cell-broken { border-style: dashed; border-color: #fca5a5; opacity: .6; cursor: default; background: #fee2e2; }
.spp-seat-cell.cell-instructor { border-color: #86efac; background: #dcfce7; cursor: default; }
.spp-seat-cell.drag-over { border-color: var(--spp-primary); background: var(--spp-primary-light); transform: scale(1.03); }
.spp-seat-cell .s-name { font-size: .8rem; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.spp-seat-cell .s-info { font-size: .7rem; color: var(--spp-muted); }
.spp-seat-cell .s-seat { position: absolute; top: 2px; right: 5px; font-size: .65rem; color: #94a3b8; }
.spp-seat-cell .s-gender { position: absolute; top: 2px; left: 5px; font-size: .6rem; padding: 0 4px; border-radius: 3px; font-weight: 700; }
.spp-seat-cell .s-gender.male { background: #dbeafe; color: #1d4ed8; }
.spp-seat-cell .s-gender.female { background: #fce7f3; color: #be185d; }
.spp-legend { display: flex; gap: 1rem; flex-wrap: wrap; margin-top: .5rem; font-size: .8rem; }
.spp-legend-item { display: flex; align-items: center; gap: .35rem; }
.spp-legend-dot { width: 14px; height: 14px; border-radius: 3px; border: 1px solid rgba(0,0,0,.1); }
.spp-search-box { position: relative; }
.spp-search-box input { padding-left: 2rem; }
.spp-search-box::before { content: '🔍'; position: absolute; left: .6rem; top: 50%; transform: translateY(-50%); font-size: .85rem; }
.spp-highlight { animation: sppPulse .8s ease 2; }
@keyframes sppPulse { 0%,100%{ box-shadow: none; } 50%{ box-shadow: 0 0 0 4px var(--spp-warning); } }

/* ── Print Styles ──────────────────────────── */
.spp-print-only { display: none; }
@media print {
  .spp-root { display: none !important; }
  .spp-print-area { display: block !important; }
  @page { size: A4; margin: 10mm; }
}
.spp-print-sheet { width: 190mm; margin: 0 auto; font-family: var(--spp-font-bn); }
.spp-print-sheet h2 { text-align: center; margin-bottom: 2mm; font-size: 14pt; }
.spp-print-sheet .meta { text-align: center; font-size: 10pt; margin-bottom: 4mm; color: #555; }
.spp-print-grid { display: grid; gap: 3px; }
.spp-print-cell { border: 1px solid #333; padding: 2mm 3mm; font-size: 8pt; text-align: center; }
.spp-print-cell .pc-name { font-weight: 700; font-size: 9pt; }
.spp-print-cell .pc-info { font-size: 7.5pt; color: #444; }
.spp-print-cell.empty-cell { border-style: dashed; color: #ccc; }
.spp-att-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
.spp-att-table th, .spp-att-table td { border: 1px solid #333; padding: 2mm 3mm; text-align: left; }
.spp-att-table th { background: #f1f5f9; font-weight: 700; }

/* ── Summary & Sticker Styles ── */
.spp-summary-title { text-align: center; font-family: 'Georgia', serif; font-size: 20pt; font-weight: bold; margin-bottom: 6mm; margin-top: 10mm; }
.spp-summary-table { width: 100%; border-collapse: collapse; margin-bottom: 8mm; font-family: 'Georgia', serif; }
.spp-summary-table th, .spp-summary-table td { border: 1px solid #000; padding: 2mm 3mm; text-align: left; }
.spp-summary-table th { background: #f8fafc; font-weight: bold; }
.spp-seat-labels-container { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3px; width: 100%; margin-top: 5mm; }
.spp-seat-label-card { border: 1px solid #000; padding: 5mm 3mm; text-align: center; font-family: system-ui, sans-serif; page-break-inside: avoid; display: flex; flex-direction: column; justify-content: center; min-height: 22mm; }

/* ── Responsive helpers ─────────────────────── */
.spp-gap-1 { gap: 1rem; }
.spp-mt-1 { margin-top: 1rem; }
.spp-mb-1 { margin-bottom: 1rem; }
.spp-text-center { text-align: center; }
.spp-text-muted { color: var(--spp-muted); font-size: .85rem; }
.spp-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: .5rem; }
.spp-badge { display: inline-block; padding: .1rem .5rem; border-radius: 999px; font-size: .75rem; font-weight: 700; }
.spp-badge-blue { background: var(--spp-primary-light); color: var(--spp-primary); }
.spp-badge-green { background: var(--spp-success-light); color: var(--spp-success); }
.spp-overflow-msg { padding: 1rem; background: var(--spp-danger-light); border: 1px solid var(--spp-danger); border-radius: var(--spp-radius); color: var(--spp-danger); font-weight: 600; margin-top: .75rem; }
`;

/* ─────────────────── CONSTANTS ─────────────────── */
const LS_ROOMS = 'seatplan_rooms';
const LS_PLANS = 'seatplan_saved_plans';
const LS_RULES = 'seatplan_fixed_rules';

const CLASS_COLORS = [
  '#dbeafe','#fce7f3','#dcfce7','#fef3c7','#e0e7ff',
  '#ccfbf1','#fde68a','#fecaca','#c7d2fe','#d9f99d',
  '#fbcfe8','#a5f3fc','#fed7aa','#e9d5ff','#bfdbfe'
];
const MALE_RE = /^(male|ছেলে|বালক|m)$/i;
const FEMALE_RE = /^(female|মেয়ে|বালিকা|f)$/i;

/* ─────────────────── UTILITIES ─────────────────── */
const $ = (id: string): HTMLElement | null => document.getElementById(id);
function uid(): string { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
function ls<T>(key: string, val?: T): T | null {
  if (val === undefined) { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } }
  localStorage.setItem(key, JSON.stringify(val));
  return null;
}
function classKey(cls: string, sec: string): string { return `${cls}-${sec}`; }
function getColRows(rm: Room): number[] {
  if (rm.col_rows && rm.col_rows.length === rm.cols) return rm.col_rows;
  return Array(rm.cols || 1).fill(rm.rows || 5);
}
function getMaxRows(rm: Room): number { return Math.max(...getColRows(rm)); }
function roomCap(rm: Room): number {
  const cr = getColRows(rm);
  const cellTypes = rm.cellTypes || {};
  let count = 0;
  for (let c = 0; c < rm.cols; c++) {
    for (let r = 0; r < cr[c]; r++) {
      const ct = cellTypes[`${r}-${c}`] || 'desk';
      if (ct === 'desk') count += (rm.bench_size || 1);
    }
  }
  return count;
}
function rawCellCount(rm: Room): number {
  return getColRows(rm).reduce((s, v) => s + v, 0);
}
function genderLabel(g: string): string {
  if (!g) return '';
  if (MALE_RE.test(g)) return 'male';
  if (FEMALE_RE.test(g)) return 'female';
  return '';
}

/* ─────────────────── MAIN RENDER ─────────────────── */
export async function renderSeatPlanProPage(app: HTMLElement): Promise<void> {
  // Inject scoped CSS
  let styleEl = document.getElementById('spp-styles');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'spp-styles';
    styleEl.textContent = SEAT_PLAN_CSS;
    document.head.appendChild(styleEl);
  }

  // State
  const state: AppState = {
    rooms: [],
    students: [],
    currentPlan: null,
    savedPlans: [],
    classOptions: [],
    classMap: new Map(),
    sectionMap: new Map(),
  };

  let editingRoomId: string | null = null;
  let fixedRules: FixedRule[] = [];
  let activeGridRoom = 0;
  let dragSource: { row: number; col: number; roomIdx: number } | null = null;
  let activeBrush: CellType = 'desk';
  let designerRoomId: string | null = null;

  /* ── Helpers ──────────────────────────── */
  function setStatus(msg: string): void {
    const el = $('sppStatusBar');
    if (el) el.textContent = msg;
  }

  function colorForClass(key: string): string {
    const keys = [...new Set(state.students.map(s => classKey(s.cls, s.section)))].sort();
    const idx = keys.indexOf(key);
    return CLASS_COLORS[idx % CLASS_COLORS.length];
  }

  function loadRooms(): void { state.rooms = ls<Room[]>(LS_ROOMS) || []; }
  function saveRooms(): void { ls(LS_ROOMS, state.rooms); }
  function loadSavedPlans(): void { state.savedPlans = ls<Plan[]>(LS_PLANS) || []; }
  function loadFixedRules(): void { fixedRules = ls<FixedRule[]>(LS_RULES) || []; }
  function saveFixedRules(): void { ls(LS_RULES, fixedRules); }

  function parseColRows(text: string, cols: number): number[] {
    const parts = text.split(',').map(s => parseInt(s.trim())).filter(n => n > 0);
    if (!parts.length) return Array(cols).fill(5);
    if (parts.length === 1) return Array(cols).fill(parts[0]);
    while (parts.length < cols) parts.push(parts[parts.length - 1]);
    return parts.slice(0, cols);
  }

  function getCellType(rm: Room, row: number, col: number): CellType {
    return (rm.cellTypes || {})[`${row}-${col}`] || 'desk';
  }

  /* ── HTML Template ──────────────────────── */
  app.innerHTML = `
<div class="spp-root">
<!-- ═══════════════ HEADER & NAVIGATION ═══════════════ -->
<div class="spp-header">
  <div class="spp-header-inner">
    <a href="/" class="spp-back-btn" id="sppBackBtn">← Back</a>
    <h1>📋 <span>ExamSeat</span> Planner Pro</h1>
    <div class="spp-tabs">
      <button class="spp-tab-btn active" data-tab="dashboard">Dashboard</button>
      <button class="spp-tab-btn" data-tab="rooms">Room Setup</button>
      <button class="spp-tab-btn" data-tab="fixed">Fixed Assign</button>
      <button class="spp-tab-btn" data-tab="planner">Seat Plan</button>
      <button class="spp-tab-btn" data-tab="export">Print</button>
    </div>
  </div>
</div>

<div class="spp-container">
  <div id="sppStatusBar" class="spp-status-bar"></div>

  <!-- ═══════════════ TAB 1: DASHBOARD ═══════════════ -->
  <div class="spp-tab-panel active" id="spp-panel-dashboard">
    <div class="spp-stats-grid" id="sppDashStats">
      <div class="spp-stat-card blue"><div class="spp-number" id="spp-ds-rooms">0</div><div class="spp-label">Configured Rooms</div></div>
      <div class="spp-stat-card green"><div class="spp-number" id="spp-ds-seats">0</div><div class="spp-label">Total Seats</div></div>
      <div class="spp-stat-card orange"><div class="spp-number" id="spp-ds-plans">0</div><div class="spp-label">Saved Plans</div></div>
      <div class="spp-stat-card red"><div class="spp-number" id="spp-ds-students">—</div><div class="spp-label">Total Students (DB)</div></div>
    </div>
    <div class="spp-card">
      <div class="spp-card-title"><span class="spp-icon">📌</span> Quick Guide</div>
      <ol style="padding-left:1.25rem; line-height:2">
        <li>Go to <strong>Room Setup</strong> tab and add exam rooms (Building, Room No, Rows × Columns).</li>
        <li>Use the <strong>Interactive Designer Brush</strong> to mark aisles, broken seats, and instructor desks.</li>
        <li>In <strong>Seat Plan</strong> tab, select Exam, Session & Classes.</li>
        <li>Choose pattern (Z / I) and options (Anti-Cheating, Gender Separation).</li>
        <li>Click <strong>"Generate Seat Plan"</strong> ⟶ View in visual grid.</li>
        <li>Drag-and-drop to manually swap seats if needed.</li>
        <li>Go to <strong>Print</strong> tab for room seat maps, attendance sheets & gate summary.</li>
      </ol>
    </div>
    <div class="spp-card spp-mt-1">
      <div class="spp-card-title"><span class="spp-icon">💾</span> Saved Plans</div>
      <div id="sppSavedPlansList" class="spp-text-muted">No saved plans yet.</div>
    </div>
  </div>

  <!-- ═══════════════ TAB 2: ROOM SETUP ═══════════════ -->
  <div class="spp-tab-panel" id="spp-panel-rooms">
    <div class="spp-card spp-mb-1">
      <div class="spp-card-title"><span class="spp-icon">🏫</span> Add New Room</div>
      <div class="spp-form-row">
        <div><label>Building Name</label><input id="sppRmBuilding" placeholder="e.g. Main Building"></div>
        <div><label>Room No</label><input id="sppRmNumber" placeholder="e.g. 101"></div>
        <div><label>Columns</label><input id="sppRmCols" type="number" min="1" max="15" value="6"></div>
        <div><label>Rows per Column</label><input id="sppRmRows" type="text" placeholder="e.g. 5 or 5,5,4,5" value="5"><div class="spp-text-muted" style="font-size:.7rem;margin-top:2px">Single number = uniform; comma-separated = per column</div></div>
        <div><label>Seats per Bench</label><input id="sppRmBench" type="number" min="1" max="6" value="1"></div>
      </div>
      <div class="spp-btn-group">
        <button class="spp-btn spp-btn-primary" id="sppAddRoomBtn">➕ Add Room</button>
        <button class="spp-btn" id="sppCancelEditBtn" style="display:none">✖ Cancel Edit</button>
        <button class="spp-btn" id="sppClearRoomsBtn">🗑️ Clear All Rooms</button>
      </div>
    </div>

    <!-- Interactive Designer Brush Panel -->
    <div class="spp-brush-panel" id="sppBrushPanel">
      <h4>🖌️ Interactive Designer Brush</h4>
      <p>Select a tool, then click directly on the seating board to design your classroom.</p>
      <div class="spp-brush-options" id="sppBrushOptions">
        <label class="spp-brush-option active" data-brush="desk">
          <input type="radio" name="sppBrush" value="desk" checked>
          <div class="spp-brush-icon desk">🪑</div>
          <div class="spp-brush-text">
            <strong>Regular Student Desk</strong>
            <span>Usable space for seating students.</span>
          </div>
        </label>
        <label class="spp-brush-option" data-brush="aisle">
          <input type="radio" name="sppBrush" value="aisle">
          <div class="spp-brush-icon aisle">🚶</div>
          <div class="spp-brush-text">
            <strong>Aisle / Walkway</strong>
            <span>Empty gaps, hallways, or dividers.</span>
          </div>
        </label>
        <label class="spp-brush-option" data-brush="broken">
          <input type="radio" name="sppBrush" value="broken">
          <div class="spp-brush-icon broken">🚫</div>
          <div class="spp-brush-text">
            <strong>Broken / Unusable Seat</strong>
            <span>Seat exists, but must remain empty.</span>
          </div>
        </label>
        <label class="spp-brush-option" data-brush="instructor">
          <input type="radio" name="sppBrush" value="instructor">
          <div class="spp-brush-icon instructor">🏫</div>
          <div class="spp-brush-text">
            <strong>Instructor Desk / Stage</strong>
            <span>Teacher station, stage, or lectern.</span>
          </div>
        </label>
      </div>
    </div>

    <div id="sppRoomList" class="spp-room-grid"></div>
  </div>

  <!-- ═══════════════ TAB 3: SEAT PLANNER ═══════════════ -->
  <div class="spp-tab-panel" id="spp-panel-planner">
    <div class="spp-planner-layout">
      <!-- Left Config Panel -->
      <div class="spp-config-panel">
        <div class="spp-card spp-mb-1">
          <div class="spp-card-title"><span class="spp-icon">⚙️</span> Configuration</div>
          <label>Exam Name</label>
          <select id="sppPlExam">
            <option>Half Yearly Examination</option>
            <option>Annual Examination</option>
            <option>Pre-Test Examination</option>
            <option>Test Examination</option>
          </select>
          <div class="spp-form-row spp-mt-1">
            <div><label>Session</label><select id="sppPlSession"><option value="">Loading…</option></select></div>
            <div><label>Year</label><select id="sppPlYear"><option>2025</option><option>2026</option></select></div>
          </div>
        </div>
        <div class="spp-card spp-mb-1">
          <div class="spp-card-title"><span class="spp-icon">👨‍🎓</span> Class-Section Selection</div>
          <div id="sppClassList" class="spp-class-list"><span class="spp-text-muted">Select a session first…</span></div>
          <div class="spp-flex-between spp-mt-1">
            <span class="spp-text-muted" id="sppSelClassCount">0 selected</span>
            <button class="spp-btn spp-btn-sm" id="sppToggleAllClasses">Select All</button>
          </div>
        </div>
        <div class="spp-card spp-mb-1">
          <div class="spp-card-title"><span class="spp-icon">🏠</span> Room Selection</div>
          <div id="sppRoomCheckList" class="spp-room-check-list"><span class="spp-text-muted">Set up rooms first…</span></div>
        </div>
        <div class="spp-card spp-mb-1">
          <div class="spp-card-title"><span class="spp-icon">🔧</span> Options</div>
          <label style="margin-bottom:.5rem">Pattern</label>
          <div class="spp-option-row">
            <label style="display:flex;align-items:center;gap:.35rem;font-weight:400"><input type="radio" name="sppPattern" value="Z" checked> Z-Pattern (row by row)</label>
          </div>
          <div class="spp-option-row">
            <label style="display:flex;align-items:center;gap:.35rem;font-weight:400"><input type="radio" name="sppPattern" value="I"> I-Pattern (column by column)</label>
          </div>
          <hr style="border:none;border-top:1px solid var(--spp-border);margin:.5rem 0">
          <div class="spp-option-row">
            <label style="display:flex;align-items:center;gap:.35rem;font-weight:400"><input type="checkbox" id="sppOptAntiCheat" checked> Anti-Cheating (different classes side-by-side)</label>
          </div>
          <div class="spp-option-row">
            <label style="display:flex;align-items:center;gap:.35rem;font-weight:400"><input type="checkbox" id="sppOptGender"> Gender Separation (boys/girls in separate blocks)</label>
          </div>
        </div>
        <button class="spp-btn spp-btn-primary" id="sppGenerateBtn" style="width:100%;padding:.7rem">🚀 Generate Seat Plan</button>
        <div class="spp-btn-group spp-mt-1">
          <button class="spp-btn spp-btn-success spp-btn-sm" id="sppSavePlanBtn" disabled>💾 Save Plan</button>
          <button class="spp-btn spp-btn-sm" id="sppClearPlanBtn">🗑️ Clear Plan</button>
        </div>
      </div>

      <!-- Right: Grid Result -->
      <div>
        <div class="spp-card" id="sppGridPanel">
          <div class="spp-grid-header">
            <div class="spp-card-title" style="margin:0"><span class="spp-icon">🪑</span> Seat Plan</div>
            <div class="spp-search-box" style="min-width:180px">
              <input type="search" id="sppSeatSearch" placeholder="Search student / roll…">
            </div>
          </div>
          <div class="spp-room-tabs" id="sppGridRoomTabs"></div>
          <div id="sppGridContainer" style="margin-top:.75rem">
            <p class="spp-text-muted spp-text-center" style="padding:3rem 0">Configure from the left panel and click "Generate Seat Plan".</p>
          </div>
          <div id="sppGridLegend" class="spp-legend"></div>
          <div id="sppOverflowMsg"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════════════ TAB 4: EXPORT / PRINT ═══════════════ -->
  <div class="spp-tab-panel" id="spp-panel-export">
    <div class="spp-card spp-mb-1">
      <div class="spp-card-title"><span class="spp-icon">🖨️</span> Print Options</div>
      <div class="spp-form-row">
        <div>
          <label>Select Room</label>
          <select id="sppExpRoom"><option value="all">All Rooms</option></select>
        </div>
        <div>
          <label>Format</label>
          <select id="sppExpFormat">
            <option value="seatmap">Room Seat Map (for door posting)</option>
            <option value="attendance">Attendance Sheet</option>
            <option value="summary">Gate Summary (class-wise room assignment)</option>
            <option value="class_summary">Class-wise Summary (রুম ও রোল রেঞ্জ সহ)</option>
            <option value="room_summary">Room-wise Summary (শ্রেণি ও রোল রেঞ্জ সহ)</option>
            <option value="seat_label">Seat Label (বেঞ্চে লাগানোর স্টিকার)</option>
            <option value="both">Seat Map + Attendance</option>
            <option value="all_formats">All Formats (All sheets & summaries)</option>
          </select>
        </div>
      </div>
      <div class="spp-btn-group">
        <button class="spp-btn spp-btn-primary" id="sppPrintBtn">🖨️ Print</button>
      </div>
      <p class="spp-text-muted spp-mt-1">💡 Generate a seat plan first. In the browser Print dialog, select "Save as PDF" to save as PDF.</p>
    </div>
    <div id="sppPrintPreviewInfo" class="spp-card" style="display:none">
      <div class="spp-card-title"><span class="spp-icon">👁️</span> Print Summary</div>
      <div id="sppPrintSummary"></div>
    </div>
  </div>

  <!-- ═══════════════ TAB 5: FIXED ASSIGNMENT ═══════════════ -->
  <div class="spp-tab-panel" id="spp-panel-fixed">
    <div class="spp-planner-layout">
      <!-- Left Config Panel -->
      <div>
        <div class="spp-card spp-mb-1">
          <div class="spp-card-title"><span class="spp-icon">⚙️</span> Session Selection</div>
          <div class="spp-form-row">
            <div><label>Session</label><select id="sppFxSession"><option value="">Loading…</option></select></div>
            <div><label>Year</label><select id="sppFxYear"><option>2025</option><option>2026</option></select></div>
          </div>
        </div>
        <div class="spp-card spp-mb-1">
          <div class="spp-card-title"><span class="spp-icon">📝</span> Add Assignment Rule</div>
          <div class="spp-form-row">
            <div><label>Class-Section</label><select id="sppFxClass"><option value="">— select —</option></select></div>
            <div><label>Roll Range</label><input id="sppFxRolls" placeholder="all or 1-20" value="all"></div>
          </div>
          <div class="spp-form-row">
            <div><label>Target Room</label><select id="sppFxRoom"><option value="">— select —</option></select></div>
            <div><label>Start Row</label><input id="sppFxStartRow" type="number" min="1" value="1"></div>
            <div><label>Start Col</label><input id="sppFxStartCol" type="number" min="1" value="1"></div>
          </div>
          <button class="spp-btn spp-btn-primary spp-btn-sm" id="sppAddRuleBtn" style="width:100%;padding:.5rem;margin-top:.5rem">➕ Add Rule</button>
        </div>
      </div>
      <!-- Right Active Rules List -->
      <div>
        <div class="spp-card">
          <div class="spp-card-title" style="margin:0"><span class="spp-icon">📋</span> Configured Rules <span class="spp-text-muted" style="font-weight:400;font-size:.8rem" id="sppRuleCount"></span></div>
          <div id="sppRulesList" class="spp-mt-1" style="max-height: 250px; overflow-y: auto;"><span class="spp-text-muted">No rules added yet.</span></div>
          <div class="spp-btn-group spp-mt-1">
            <button class="spp-btn spp-btn-danger spp-btn-sm" id="sppClearRulesBtn">🗑️ Clear All Rules</button>
          </div>
        </div>
        
        <div class="spp-card spp-mt-1">
          <div class="spp-card-title"><span class="spp-icon">💡</span> How it works</div>
          <p style="font-size:.85rem; line-height:1.6; margin: 0 0 .5rem 0;">
            Here you can pre-assign specific classes/sections or roll ranges to specific rooms. 
          </p>
          <p style="font-size:.85rem; line-height:1.6; margin: 0 0 .5rem 0;">
            When you are done adding rules, switch to the <strong>Seat Plan</strong> tab, select your rooms and options, and click <strong>🚀 Generate Seat Plan</strong>.
          </p>
          <p style="font-size:.85rem; line-height:1.6; font-weight: 600; color: var(--spp-success); margin: 0;">
            The generator will automatically apply these Fixed rules first, and then seamlessly seat the remaining students in the rest of the available benches!
          </p>
        </div>
      </div>
    </div>
  </div>

</div><!-- end container -->
</div><!-- end spp-root -->

<!-- PRINT AREA (hidden on screen) -->
<div id="sppPrintArea" class="spp-print-only spp-print-area"></div>
`;

  /* ─────────────────── TAB NAVIGATION ─────────────────── */
  document.querySelectorAll('.spp-tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.spp-tab-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.spp-tab-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      const tab = (btn as HTMLElement).dataset.tab;
      const panel = $('spp-panel-' + tab);
      if (panel) panel.classList.add('active');
      if (tab === 'dashboard') refreshDashboard();
      if (tab === 'rooms') renderRoomCards();
      if (tab === 'planner') refreshRoomCheckList();
      if (tab === 'fixed') refreshFixedTab();
      if (tab === 'export') refreshExportPanel();
    });
  });

  /* ─────────────────── BRUSH SELECTOR ─────────────────── */
  document.querySelectorAll('.spp-brush-option').forEach(opt => {
    opt.addEventListener('click', () => {
      document.querySelectorAll('.spp-brush-option').forEach(o => o.classList.remove('active'));
      opt.classList.add('active');
      const radio = opt.querySelector('input[type=radio]') as HTMLInputElement;
      if (radio) { radio.checked = true; activeBrush = radio.value as CellType; }
    });
  });

  /* ─────────────────── SUPABASE LOADING ─────────────────── */
  async function loadSessionFilter(): Promise<void> {
    setStatus('Loading filters…');
    try {
      const { data, error } = await sb.from('student_database')
        .select('session, active_class, active_section, status, student_gender')
        .not('active_class', 'is', null).not('active_section', 'is', null);
      if (error) throw error;

      const sessionSet = new Set<string>();
      state.classMap = new Map();
      state.sectionMap = new Map();
      (data || []).forEach((r: any) => {
        if (String(r.status || '').toUpperCase().includes('TC')) return;
        const s = String(r.session || '').trim();
        const c = String(r.active_class || '').trim();
        const sec = String(r.active_section || '').trim();
        if (!s || !c || !sec) return;
        sessionSet.add(s);
        if (!state.classMap.has(s)) state.classMap.set(s, new Set());
        state.classMap.get(s)!.add(classKey(c, sec));
        const key = s + '|' + c;
        if (!state.sectionMap.has(key)) state.sectionMap.set(key, new Set());
        state.sectionMap.get(key)!.add(sec);
      });

      const sessionSel = $('sppPlSession') as HTMLSelectElement;
      sessionSel.innerHTML = '<option value="">— Session —</option>';
      [...sessionSet].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).forEach(s => {
        const o = document.createElement('option'); o.value = s; o.textContent = s; sessionSel.appendChild(o);
      });
      setStatus('Filters loaded');
    } catch (e: any) {
      console.error(e);
      setStatus('Filter load failed: ' + e.message);
    }
  }

  function updateClassList(): void {
    const sess = ($('sppPlSession') as HTMLSelectElement).value;
    const div = $('sppClassList')!;
    div.innerHTML = '';
    if (!sess) { div.innerHTML = '<span class="spp-text-muted">Select a session first…</span>'; return; }
    const classes = state.classMap.get(sess);
    if (!classes || !classes.size) { div.innerHTML = '<span class="spp-text-muted">No classes found</span>'; return; }
    const sorted = [...classes].sort((a, b) => {
      const [ca] = a.split('-'), [cb] = b.split('-');
      const diff = (Number(ca) || 0) - (Number(cb) || 0);
      return diff !== 0 ? diff : a.localeCompare(b);
    });
    sorted.forEach(key => {
      const [cls, sec] = key.split('-');
      const item = document.createElement('label');
      item.className = 'spp-class-item';
      item.innerHTML = `<input type="checkbox" value="${key}" class="spp-cls-check"> Class ${cls} — Section ${sec}`;
      div.appendChild(item);
    });
    updateSelClassCount();
  }

  function updateSelClassCount(): void {
    const checked = document.querySelectorAll('.spp-cls-check:checked').length;
    const el = $('sppSelClassCount');
    if (el) el.textContent = `${checked} selected`;
  }

  async function loadStudentsForPlan(): Promise<void> {
    const sess = ($('sppPlSession') as HTMLSelectElement).value;
    const checked = [...document.querySelectorAll('.spp-cls-check:checked')].map(el => (el as HTMLInputElement).value);
    if (!sess || !checked.length) { state.students = []; return; }

    setStatus('Loading students…');
    state.students = [];
    try {
      const { data, error } = await sb.from('student_database')
        .select('iid, student_name_en, student_name_bn, father_name_en, group, status, session, active_class, active_section, active_roll, student_gender')
        .eq('session', sess)
        .not('active_roll', 'is', null);
      if (error) throw error;

      const wantedKeys = new Set(checked);
      for (const r of (data || [])) {
        if (String(r.status || '').toUpperCase().includes('TC')) continue;
        const cls = String(r.active_class || '').trim();
        const sec = String(r.active_section || '').trim();
        if (!wantedKeys.has(classKey(cls, sec))) continue;
        state.students.push({
          iid: r.iid || '', name: String(r.student_name_en || r.student_name_bn || ''),
          nameBn: String(r.student_name_bn || ''), father: String(r.father_name_en || ''),
          cls, section: sec, roll: String(r.active_roll || ''),
          group: String(r.group || ''), gender: genderLabel(String(r.student_gender || ''))
        });
      }
      setStatus(`${state.students.length} students loaded`);
    } catch (e: any) {
      console.error(e);
      setStatus('Student load failed: ' + e.message);
    }
  }

  /* ─────────────────── ROOM MANAGEMENT ─────────────────── */
  function addRoom(): void {
    const building = ($('sppRmBuilding') as HTMLInputElement).value.trim();
    const room_no = ($('sppRmNumber') as HTMLInputElement).value.trim();
    const cols = parseInt(($('sppRmCols') as HTMLInputElement).value) || 6;
    const col_rows = parseColRows(($('sppRmRows') as HTMLInputElement).value, cols);
    const rows = Math.max(...col_rows);
    const bench_size = parseInt(($('sppRmBench') as HTMLInputElement).value) || 1;
    if (!building || !room_no) { alert('Enter building name and room number'); return; }
    if (editingRoomId) {
      const rm = state.rooms.find(r => r.id === editingRoomId);
      if (rm) { rm.building = building; rm.room_no = room_no; rm.cols = cols; rm.col_rows = col_rows; rm.rows = rows; rm.bench_size = bench_size; }
      cancelEdit();
      setStatus(`Room ${room_no} updated`);
    } else {
      state.rooms.push({ id: uid(), building, room_no, rows, cols, col_rows, bench_size, cellTypes: {} });
      setStatus(`Room ${room_no} added`);
    }
    saveRooms();
    ($('sppRmBuilding') as HTMLInputElement).value = '';
    ($('sppRmNumber') as HTMLInputElement).value = '';
    renderRoomCards();
  }

  function editRoom(id: string): void {
    const rm = state.rooms.find(r => r.id === id);
    if (!rm) return;
    editingRoomId = id;
    ($('sppRmBuilding') as HTMLInputElement).value = rm.building;
    ($('sppRmNumber') as HTMLInputElement).value = rm.room_no;
    ($('sppRmCols') as HTMLInputElement).value = String(rm.cols);
    const cr = getColRows(rm);
    ($('sppRmRows') as HTMLInputElement).value = cr.every(v => v === cr[0]) ? String(cr[0]) : cr.join(',');
    ($('sppRmBench') as HTMLInputElement).value = String(rm.bench_size || 1);
    $('sppAddRoomBtn')!.textContent = '✏️ Update Room';
    $('sppCancelEditBtn')!.style.display = '';
    ($('sppRmBuilding') as HTMLInputElement).focus();
  }

  function cancelEdit(): void {
    editingRoomId = null;
    $('sppAddRoomBtn')!.textContent = '➕ Add Room';
    $('sppCancelEditBtn')!.style.display = 'none';
  }

  function deleteRoom(id: string): void {
    if (!confirm('Delete this room?')) return;
    state.rooms = state.rooms.filter(r => r.id !== id);
    saveRooms(); renderRoomCards();
  }

  function renderRoomCards(): void {
    const wrap = $('sppRoomList')!;
    wrap.innerHTML = '';
    if (!state.rooms.length) { wrap.innerHTML = '<p class="spp-text-muted">No rooms configured. Add rooms above.</p>'; return; }
    state.rooms.forEach(rm => {
      const div = document.createElement('div');
      div.className = 'spp-card spp-room-card';
      const bs = rm.bench_size || 1;
      const cap = roomCap(rm);
      const totalCells = rawCellCount(rm);
      const cr = getColRows(rm);
      const maxR = Math.max(...cr);
      const isUniform = cr.every(v => v === cr[0]);
      const cellTypes = rm.cellTypes || {};

      let previewHTML = '';
      for (let r = 0; r < maxR; r++) {
        for (let c = 0; c < rm.cols; c++) {
          if (r < cr[c]) {
            const ct = cellTypes[`${r}-${c}`] || 'desk';
            const ctClass = ct !== 'desk' ? ` ${ct}` : '';
            const emoji = ct === 'desk' ? '' : ct === 'aisle' ? '🚶' : ct === 'broken' ? '🚫' : '🏫';
            previewHTML += `<div class="spp-seat-dot${ctClass}" data-room-id="${rm.id}" data-row="${r}" data-col="${c}" title="Row ${r + 1}, Col ${c + 1}: ${ct}">${emoji}</div>`;
          } else {
            previewHTML += '<div style="visibility:hidden"></div>';
          }
        }
      }

      div.innerHTML = `
        <div class="spp-flex-between">
          <div><strong>${rm.building}</strong> — Room ${rm.room_no}</div>
          <span class="spp-badge spp-badge-blue">${cap} seats (${totalCells} cells)</span>
        </div>
        <div class="spp-room-meta">${isUniform ? cr[0] + ' rows' : 'rows: ' + cr.join(',')} × ${rm.cols} cols${bs > 1 ? ` × ${bs}/bench` : ''}</div>
        <div class="spp-room-preview" style="grid-template-columns:repeat(${rm.cols},1fr)">
          ${previewHTML}
        </div>
        <div class="spp-room-actions">
          <button class="spp-btn spp-btn-sm spp-edit-room-btn" data-id="${rm.id}">✏️ Edit</button>
          <button class="spp-btn spp-btn-danger spp-btn-sm spp-delete-room-btn" data-id="${rm.id}">🗑️ Delete</button>
        </div>`;
      wrap.appendChild(div);
    });

    // Attach room card button handlers
    wrap.querySelectorAll('.spp-edit-room-btn').forEach(btn => {
      btn.addEventListener('click', () => editRoom((btn as HTMLElement).dataset.id!));
    });
    wrap.querySelectorAll('.spp-delete-room-btn').forEach(btn => {
      btn.addEventListener('click', () => deleteRoom((btn as HTMLElement).dataset.id!));
    });

    // Attach brush click handlers to seat dots
    wrap.querySelectorAll('.spp-seat-dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const el = dot as HTMLElement;
        const roomId = el.dataset.roomId!;
        const row = parseInt(el.dataset.row!);
        const col = parseInt(el.dataset.col!);
        const rm = state.rooms.find(r => r.id === roomId);
        if (!rm) return;
        if (!rm.cellTypes) rm.cellTypes = {};
        rm.cellTypes[`${row}-${col}`] = activeBrush;
        saveRooms();
        renderRoomCards();
        setStatus(`Cell ${row + 1}-${col + 1} set to ${activeBrush}`);
      });
    });
  }

  /* ─────────────────── PLANNER: ROOM CHECKLIST ─────────────────── */
  function refreshRoomCheckList(): void {
    const wrap = $('sppRoomCheckList')!;
    wrap.innerHTML = '';
    if (!state.rooms.length) { wrap.innerHTML = '<span class="spp-text-muted">Set up rooms first</span>'; return; }
    state.rooms.forEach(rm => {
      const lbl = document.createElement('label');
      lbl.className = 'spp-class-item';
      const cap = roomCap(rm);
      lbl.innerHTML = `<input type="checkbox" value="${rm.id}" class="spp-room-check" checked> ${rm.building} — ${rm.room_no} (${cap} seats)`;
      wrap.appendChild(lbl);
    });
  }

  /* ─────────────────── SEAT PLAN ALGORITHM ─────────────────── */
  function interleaveByClass(students: Student[]): Student[] {
    const groups = new Map<string, Student[]>();
    students.forEach(s => {
      const key = classKey(s.cls, s.section);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(s);
    });
    groups.forEach(arr => arr.sort((a, b) => {
      const ra = Number(a.roll), rb = Number(b.roll);
      if (Number.isFinite(ra) && Number.isFinite(rb)) return ra - rb;
      return String(a.roll).localeCompare(String(b.roll));
    }));

    const keys = [...groups.keys()].sort((a, b) => {
      const [ca] = a.split('-'), [cb] = b.split('-');
      return (Number(ca) || 0) - (Number(cb) || 0) || a.localeCompare(b);
    });
    const result: Student[] = [];
    const indices: Record<string, number> = {};
    keys.forEach(k => indices[k] = 0);
    let done = false;
    while (!done) {
      done = true;
      for (const k of keys) {
        const arr = groups.get(k)!;
        if (indices[k] < arr.length) {
          result.push(arr[indices[k]++]);
          done = false;
        }
      }
    }
    return result;
  }

  function generatePlan(): void {
    const pattern = (document.querySelector('input[name=sppPattern]:checked') as HTMLInputElement)?.value || 'Z';
    const antiCheat = ($('sppOptAntiCheat') as HTMLInputElement).checked;
    const genderSep = ($('sppOptGender') as HTMLInputElement).checked;

    const selRoomIds = new Set([...document.querySelectorAll('.spp-room-check:checked')].map(el => (el as HTMLInputElement).value));
    const selRooms = state.rooms.filter(r => selRoomIds.has(r.id));

    if (!selRooms.length) { alert('Select at least one room'); return; }
    if (!state.students.length) { alert('No students loaded. Select classes first.'); return; }

    const totalCapacity = selRooms.reduce((s, r) => s + roomCap(r), 0);
    const totalStudents = state.students.length;
    if (totalStudents > totalCapacity) {
      if (!confirm(`⚠️ Students: ${totalStudents} but total seats: ${totalCapacity}.\n${totalStudents - totalCapacity} students cannot be seated.\nContinue anyway?`)) return;
    }

    let ordered: Student[];
    if (genderSep) {
      const boys = state.students.filter(s => s.gender === 'male');
      const girls = state.students.filter(s => s.gender === 'female');
      const others = state.students.filter(s => s.gender !== 'male' && s.gender !== 'female');
      ordered = [...(antiCheat ? interleaveByClass(boys) : boys), ...(antiCheat ? interleaveByClass(girls) : girls), ...(antiCheat ? interleaveByClass(others) : others)];
    } else {
      ordered = antiCheat ? interleaveByClass(state.students) : [...state.students];
    }

    const plan: Plan = {
      id: uid(),
      exam: ($('sppPlExam') as HTMLSelectElement).value,
      year: ($('sppPlYear') as HTMLSelectElement).value,
      session: ($('sppPlSession') as HTMLSelectElement).value,
      pattern, antiCheat, genderSep,
      created: new Date().toISOString(),
      rooms: [],
      unassigned: []
    };

    // Pre-initialize empty seats map for all selected rooms
    const roomSeatsMap: Record<string, SeatEntry[]> = {};
    for (const room of selRooms) {
      const bs = room.bench_size || 1;
      const cr = getColRows(room);
      const maxR = getMaxRows(room);
      const cellTypes = room.cellTypes || {};
      const seatOrder: [number, number][] = [];

      if (pattern === 'Z') {
        for (let r = 0; r < maxR; r++)
          for (let c = 0; c < room.cols; c++)
            if (r < cr[c]) seatOrder.push([r, c]);
      } else {
        for (let c = 0; c < room.cols; c++)
          for (let r = 0; r < cr[c]; r++)
            seatOrder.push([r, c]);
      }

      const seats: SeatEntry[] = [];
      for (const [r, c] of seatOrder) {
        seats.push({ row: r, col: c, students: Array(bs).fill(null) });
      }
      roomSeatsMap[room.id] = seats;
    }

    // --- Stage 1: Apply Fixed Assignment Rules first ---
    const placed = new Set<string>();
    const selClassKeys = new Set([...document.querySelectorAll('.spp-cls-check:checked')].map(el => (el as HTMLInputElement).value));
    
    // Filter rules active for current selection
    const activeRules = fixedRules.filter(r => selClassKeys.has(r.classSection) && selRoomIds.has(r.roomId));

    for (const rule of activeRules) {
      const room = selRooms.find(r => r.id === rule.roomId);
      if (!room) continue;

      const rollRange = parseRollRange(rule.rollRange);
      // Filter matching students that are not yet placed
      const matching = ordered.filter(s => {
        if (placed.has(s.iid)) return false;
        if (classKey(s.cls, s.section) !== rule.classSection) return false;
        return matchesRollRange(s, rollRange);
      });

      if (!matching.length) continue;

      // Sort matching students by roll ascending
      matching.sort((a, b) => {
        const ra = Number(a.roll), rb = Number(b.roll);
        if (Number.isFinite(ra) && Number.isFinite(rb)) return ra - rb;
        return String(a.roll).localeCompare(String(b.roll));
      });

      const seats = roomSeatsMap[rule.roomId];
      if (!seats) continue;

      const bs = room.bench_size || 1;
      const cr = getColRows(room);
      const maxR = getMaxRows(room);
      const cellTypes = room.cellTypes || {};
      const startR = rule.startRow - 1;
      const startC = rule.startCol - 1;

      const fullOrder: [number, number][] = [];
      if (pattern === 'Z') {
        for (let r = 0; r < maxR; r++)
          for (let c = 0; c < room.cols; c++)
            if (r < cr[c]) fullOrder.push([r, c]);
      } else {
        for (let c = 0; c < room.cols; c++)
          for (let r = 0; r < cr[c]; r++)
            fullOrder.push([r, c]);
      }

      // Filter out non-desk cells
      const deskOrder = fullOrder.filter(([r, c]) => (cellTypes[`${r}-${c}`] || 'desk') === 'desk');

      const startIdx = deskOrder.findIndex(([r, c]) => {
        if (pattern === 'Z') return r > startR || (r === startR && c >= startC);
        return c > startC || (c === startC && r >= startR);
      });
      const seatOrder = startIdx >= 0 ? deskOrder.slice(startIdx) : deskOrder;

      let sPtr = 0;
      for (const [r, c] of seatOrder) {
        if (sPtr >= matching.length) break;
        const seat = seats.find(s => s.row === r && s.col === c);
        if (!seat) continue;
        for (let b = 0; b < bs; b++) {
          if (sPtr >= matching.length) break;
          if (seat.students[b] === null) {
            seat.students[b] = matching[sPtr];
            placed.add(matching[sPtr].iid);
            sPtr++;
          }
        }
      }
    }

    // --- Stage 2: Distribute remaining students to remaining seats ---
    const unassignedStudents = ordered.filter(s => !placed.has(s.iid));
    let ptr = 0;

    for (const room of selRooms) {
      const seats = roomSeatsMap[room.id];
      if (!seats) continue;
      const bs = room.bench_size || 1;
      const cr = getColRows(room);
      const maxR = getMaxRows(room);
      const cellTypes = room.cellTypes || {};

      const fullOrder: [number, number][] = [];
      if (pattern === 'Z') {
        for (let r = 0; r < maxR; r++)
          for (let c = 0; c < room.cols; c++)
            if (r < cr[c]) fullOrder.push([r, c]);
      } else {
        for (let c = 0; c < room.cols; c++)
          for (let r = 0; r < cr[c]; r++)
            fullOrder.push([r, c]);
      }

      const deskOrder = fullOrder.filter(([r, c]) => (cellTypes[`${r}-${c}`] || 'desk') === 'desk');

      for (const [r, c] of deskOrder) {
        if (ptr >= unassignedStudents.length) break;
        const seat = seats.find(s => s.row === r && s.col === c);
        if (!seat) continue;
        for (let b = 0; b < bs; b++) {
          if (ptr >= unassignedStudents.length) break;
          if (seat.students[b] === null) {
            seat.students[b] = unassignedStudents[ptr++];
          }
        }
      }
    }

    // Save final seated positions back to plan
    for (const room of selRooms) {
      plan.rooms.push({ room_id: room.id, room_info: { ...room }, seats: roomSeatsMap[room.id] });
    }

    if (ptr < unassignedStudents.length) {
      plan.unassigned = unassignedStudents.slice(ptr);
    }

    state.currentPlan = plan;
    ($('sppSavePlanBtn') as HTMLButtonElement).disabled = false;
    renderGrid();
    setStatus(`Seat plan generated! ${placed.size + ptr} seated, ${plan.unassigned.length} remaining`);
  }

  /* ─────────────────── GRID RENDERING ─────────────────── */
  function renderGrid(): void {
    const plan = state.currentPlan;
    if (!plan) { $('sppGridContainer')!.innerHTML = '<p class="spp-text-muted spp-text-center" style="padding:3rem 0">Generate a plan first.</p>'; return; }

    const tabsWrap = $('sppGridRoomTabs')!;
    tabsWrap.innerHTML = '';
    plan.rooms.forEach((pr, i) => {
      const btn = document.createElement('button');
      btn.className = 'spp-room-tab' + (i === activeGridRoom ? ' active' : '');
      btn.textContent = `${pr.room_info.building} — ${pr.room_info.room_no}`;
      btn.addEventListener('click', () => { activeGridRoom = i; renderGrid(); });
      tabsWrap.appendChild(btn);
    });

    if (activeGridRoom >= plan.rooms.length) activeGridRoom = 0;
    const pr = plan.rooms[activeGridRoom];
    renderRoomGrid(pr);

    const keys = [...new Set(state.students.map(s => classKey(s.cls, s.section)))].sort();
    $('sppGridLegend')!.innerHTML = keys.map(k => {
      const bg = colorForClass(k);
      return `<div class="spp-legend-item"><div class="spp-legend-dot" style="background:${bg}"></div> Class ${k}</div>`;
    }).join('');

    const ovWrap = $('sppOverflowMsg')!;
    if (plan.unassigned.length) {
      ovWrap.innerHTML = `<div class="spp-overflow-msg">⚠️ ${plan.unassigned.length} students could not be seated — add more rooms or increase capacity.</div>`;
    } else { ovWrap.innerHTML = ''; }
  }

  function renderRoomGrid(pr: PlanRoom): void {
    const wrap = $('sppGridContainer')!;
    const cols = pr.room_info.cols;
    const cr = getColRows(pr.room_info);
    const maxR = getMaxRows(pr.room_info);
    const bs = pr.room_info.bench_size || 1;
    const cellTypes = pr.room_info.cellTypes || {};
    wrap.innerHTML = '';

    const total = roomCap(pr.room_info);
    const filled = pr.seats.reduce((s, seat) => s + seat.students.filter(st => st).length, 0);
    const statsDiv = document.createElement('div');
    statsDiv.className = 'spp-flex-between spp-mb-1';
    statsDiv.innerHTML = `<span class="spp-text-muted">${pr.room_info.building} — Room ${pr.room_info.room_no}</span>
      <span><span class="spp-badge spp-badge-green">${filled}/${total} seats filled</span></span>`;
    wrap.appendChild(statsDiv);

    const grid = document.createElement('div');
    grid.className = 'spp-seat-grid';
    grid.style.gridTemplateColumns = `repeat(${cols}, minmax(110px, 1fr))`;
    grid.id = 'sppActiveSeatGrid';

    const lookup: Record<string, SeatEntry> = {};
    pr.seats.forEach(s => { lookup[s.row + '-' + s.col] = s; });

    for (let r = 0; r < maxR; r++) {
      for (let c = 0; c < cols; c++) {
        if (r >= cr[c]) { grid.appendChild(document.createElement('div')); continue; }

        const ct = cellTypes[`${r}-${c}`] || 'desk';
        const seat = lookup[r + '-' + c];
        const cell = document.createElement('div');
        cell.className = 'spp-seat-cell';
        cell.dataset.row = String(r);
        cell.dataset.col = String(c);
        cell.dataset.roomIdx = String(activeGridRoom);

        // Handle non-desk cell types
        if (ct === 'aisle') {
          cell.classList.add('cell-aisle');
          cell.innerHTML = `<div class="s-seat">${r + 1}-${c + 1}</div><div class="spp-text-muted spp-text-center" style="font-size:.7rem">🚶 Aisle</div>`;
          grid.appendChild(cell);
          continue;
        }
        if (ct === 'broken') {
          cell.classList.add('cell-broken');
          cell.innerHTML = `<div class="s-seat">${r + 1}-${c + 1}</div><div class="spp-text-muted spp-text-center" style="font-size:.7rem">🚫 Broken</div>`;
          grid.appendChild(cell);
          continue;
        }
        if (ct === 'instructor') {
          cell.classList.add('cell-instructor');
          cell.innerHTML = `<div class="s-seat">${r + 1}-${c + 1}</div><div class="spp-text-center" style="font-size:.75rem;font-weight:700;color:#15803d">🏫 Instructor</div>`;
          grid.appendChild(cell);
          continue;
        }

        const studs = seat ? seat.students : [];
        const hasAny = studs.some(s => s);

        if (hasAny) {
          const first = studs.find(s => s)!;
          const bg = colorForClass(classKey(first.cls, first.section));
          cell.classList.add('occupied');
          if (bs <= 1) cell.style.background = bg;
          cell.draggable = true;

          let html = `<div class="s-seat">${r + 1}-${c + 1}</div>`;
          studs.forEach(s => {
            if (s) {
              const sbg = colorForClass(classKey(s.cls, s.section));
              html += `<div class="bench-student" ${bs > 1 ? `style="background:${sbg};border-radius:4px;padding:2px 4px;margin:1px 0"` : ''}>
                ${s.gender ? `<span class="s-gender ${s.gender}" style="position:static;display:inline">${s.gender === 'male' ? '♂' : '♀'}</span> ` : ''}
                <span class="s-name" title="${s.name}">${s.name}</span>
                <div class="s-info">${s.cls}-${s.section} | Roll: ${s.roll}</div>
              </div>`;
            } else {
              html += `<div class="bench-student spp-text-muted" style="font-size:.7rem">— empty —</div>`;
            }
          });
          cell.innerHTML = html;
          cell.addEventListener('dragstart', onDragStart);
        } else {
          cell.classList.add('empty');
          cell.innerHTML = `<div class="s-seat">${r + 1}-${c + 1}</div><div class="spp-text-muted spp-text-center" style="font-size:.75rem">empty</div>`;
        }

        cell.addEventListener('dragover', onDragOver);
        cell.addEventListener('dragleave', onDragLeave);
        cell.addEventListener('drop', onDrop);
        grid.appendChild(cell);
      }
    }
    wrap.appendChild(grid);
  }

  /* ─────────────────── DRAG & DROP ─────────────────── */
  function onDragStart(e: DragEvent): void {
    const el = e.currentTarget as HTMLElement;
    dragSource = { row: +el.dataset.row!, col: +el.dataset.col!, roomIdx: +el.dataset.roomIdx! };
    e.dataTransfer!.effectAllowed = 'move';
    e.dataTransfer!.setData('text/plain', '');
  }

  function onDragOver(e: DragEvent): void {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';
    (e.currentTarget as HTMLElement).classList.add('drag-over');
  }

  function onDragLeave(e: DragEvent): void {
    (e.currentTarget as HTMLElement).classList.remove('drag-over');
  }

  function onDrop(e: DragEvent): void {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove('drag-over');
    if (!dragSource || !state.currentPlan) return;

    const el = e.currentTarget as HTMLElement;
    const targetRow = +el.dataset.row!;
    const targetCol = +el.dataset.col!;
    const targetRoomIdx = +el.dataset.roomIdx!;

    if (dragSource.roomIdx !== targetRoomIdx) return;

    const pr = state.currentPlan.rooms[targetRoomIdx];
    const srcSeat = pr.seats.find(s => s.row === dragSource!.row && s.col === dragSource!.col);
    const tgtSeat = pr.seats.find(s => s.row === targetRow && s.col === targetCol);

    if (!srcSeat || !tgtSeat) return;
    const tmp = [...srcSeat.students];
    srcSeat.students = [...tgtSeat.students];
    tgtSeat.students = tmp;

    dragSource = null;
    renderGrid();
    setStatus('Seats swapped');
  }

  /* ─────────────────── SEARCH ─────────────────── */
  function searchStudent(): void {
    const q = ($('sppSeatSearch') as HTMLInputElement).value.trim().toLowerCase();
    if (!q || !state.currentPlan) return;

    for (let ri = 0; ri < state.currentPlan.rooms.length; ri++) {
      const pr = state.currentPlan.rooms[ri];
      for (const seat of pr.seats) {
        for (const s of seat.students) {
          if (!s) continue;
          if (s.name.toLowerCase().includes(q) || s.roll.includes(q) || String(s.iid).includes(q)) {
            activeGridRoom = ri;
            renderGrid();
            setTimeout(() => {
              const cell = document.querySelector(`.spp-seat-cell[data-row="${seat.row}"][data-col="${seat.col}"]`);
              if (cell) { (cell as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' }); cell.classList.add('spp-highlight'); }
            }, 100);
            setStatus(`Found: ${s.name} — Room ${pr.room_info.room_no}, Seat ${seat.row + 1}-${seat.col + 1}`);
            return;
          }
        }
      }
    }
    setStatus('Student not found');
  }

  /* ─────────────────── SAVE / LOAD PLANS ─────────────────── */
  function savePlan(): void {
    if (!state.currentPlan) { alert('Generate a plan first'); return; }
    state.savedPlans.push({ ...state.currentPlan });
    ls(LS_PLANS, state.savedPlans);
    refreshDashboard();
    setStatus('Plan saved ✓');
  }

  function loadPlan(idx: number): void {
    const p = state.savedPlans[idx];
    if (!p) return;
    state.currentPlan = JSON.parse(JSON.stringify(p));
    state.students = [];
    state.currentPlan!.rooms.forEach(pr => pr.seats.forEach(s => { (s.students || []).forEach(st => { if (st) state.students.push(st); }); }));
    activeGridRoom = 0;
    ($('sppSavePlanBtn') as HTMLButtonElement).disabled = false;
    // Switch to planner tab
    (document.querySelector('.spp-tab-btn[data-tab=planner]') as HTMLElement)?.click();
    renderGrid();
    setStatus('Saved plan loaded');
  }

  function deletePlan(idx: number): void {
    if (!confirm('Delete this plan?')) return;
    state.savedPlans.splice(idx, 1);
    ls(LS_PLANS, state.savedPlans);
    refreshDashboard();
  }

  function refreshDashboard(): void {
    loadSavedPlans();
    const dsRooms = $('spp-ds-rooms');
    const dsSeats = $('spp-ds-seats');
    const dsPlans = $('spp-ds-plans');
    if (dsRooms) dsRooms.textContent = String(state.rooms.length);
    if (dsSeats) dsSeats.textContent = String(state.rooms.reduce((s, r) => s + roomCap(r), 0));
    if (dsPlans) dsPlans.textContent = String(state.savedPlans.length);

    const wrap = $('sppSavedPlansList')!;
    if (!state.savedPlans.length) { wrap.innerHTML = '<span class="spp-text-muted">No saved plans yet.</span>'; return; }
    wrap.innerHTML = '';
    state.savedPlans.forEach((p, i) => {
      const div = document.createElement('div');
      div.className = 'spp-flex-between';
      div.style.cssText = 'padding:.5rem 0; border-bottom:1px solid var(--spp-border)';
      const totalStudents = p.rooms.reduce((s, r) => s + r.seats.reduce((a, seat) => a + seat.students.filter(st => st).length, 0), 0);
      div.innerHTML = `
        <div>
          <strong>${p.exam}</strong> — ${p.session} | ${p.year}
          <div class="spp-text-muted" style="font-size:.8rem">${p.rooms.length} rooms, ${totalStudents} students | ${new Date(p.created).toLocaleDateString('en-US')}</div>
        </div>
        <div class="spp-btn-group">
          <button class="spp-btn spp-btn-sm spp-btn-primary spp-load-plan-btn" data-idx="${i}">📂 Load</button>
          <button class="spp-btn spp-btn-sm spp-btn-danger spp-delete-plan-btn" data-idx="${i}">🗑️</button>
        </div>`;
      wrap.appendChild(div);
    });
    wrap.querySelectorAll('.spp-load-plan-btn').forEach(btn => {
      btn.addEventListener('click', () => loadPlan(parseInt((btn as HTMLElement).dataset.idx!)));
    });
    wrap.querySelectorAll('.spp-delete-plan-btn').forEach(btn => {
      btn.addEventListener('click', () => deletePlan(parseInt((btn as HTMLElement).dataset.idx!)));
    });
  }

  /* ─────────────────── EXPORT / PRINT ─────────────────── */
  function refreshExportPanel(): void {
    const sel = $('sppExpRoom') as HTMLSelectElement;
    sel.innerHTML = '<option value="all">All Rooms</option>';
    if (state.currentPlan) {
      state.currentPlan.rooms.forEach((pr, i) => {
        const o = document.createElement('option');
        o.value = String(i); o.textContent = `${pr.room_info.building} — ${pr.room_info.room_no}`;
        sel.appendChild(o);
      });
      $('sppPrintPreviewInfo')!.style.display = 'block';
      const total = state.currentPlan.rooms.reduce((s, r) => s + r.seats.reduce((a, seat) => a + seat.students.filter(st => st).length, 0), 0);
      $('sppPrintSummary')!.innerHTML = `<strong>${state.currentPlan.exam}</strong> | Session: ${state.currentPlan.session} | Year: ${state.currentPlan.year}<br>
        ${state.currentPlan.rooms.length} rooms, ${total} students seated`;
    } else {
      $('sppPrintPreviewInfo')!.style.display = 'none';
    }
  }

  function getRollRanges(rolls: string[]): string {
    const nums = rolls.map(r => parseInt(r.trim())).filter(n => !isNaN(n)).sort((a, b) => a - b);
    const nonNums = rolls.filter(r => isNaN(parseInt(r.trim()))).map(r => r.trim()).sort();
    if (nums.length === 0) return nonNums.join(', ');

    const ranges: string[] = [];
    let start = nums[0];
    let prev = nums[0];

    for (let i = 1; i < nums.length; i++) {
      if (nums[i] === prev + 1) {
        prev = nums[i];
      } else {
        if (start === prev) ranges.push(String(start));
        else ranges.push(`${start}-${prev}`);
        start = nums[i];
        prev = nums[i];
      }
    }
    if (start === prev) ranges.push(String(start));
    else ranges.push(`${start}-${prev}`);

    const combined = [...ranges, ...nonNums];
    return combined.join(',') + (combined.length > 0 ? ',' : '');
  }

  function buildClassWiseSummary(plan: Plan, rooms: PlanRoom[]): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'spp-print-sheet';
    sheet.style.pageBreakAfter = 'always';

    const title = document.createElement('h2');
    title.className = 'spp-summary-title';
    title.textContent = 'Class-wise summary';
    sheet.appendChild(title);

    // Group rolls by classKey -> roomLabel
    const classMap: Record<string, Record<string, string[]>> = {};
    rooms.forEach(pr => {
      const roomLabel = `${pr.room_info.building}:${pr.room_info.room_no}`;
      pr.seats.forEach(seat => {
        (seat.students || []).forEach(s => {
          if (!s) return;
          const key = classKey(s.cls, s.section);
          if (!classMap[key]) classMap[key] = {};
          if (!classMap[key][roomLabel]) classMap[key][roomLabel] = [];
          classMap[key][roomLabel].push(s.roll);
        });
      });
    });

    const keys = Object.keys(classMap).sort((a, b) => {
      const [ca] = a.split('-'), [cb] = b.split('-');
      return (Number(ca) || 0) - (Number(cb) || 0) || a.localeCompare(b);
    });

    keys.forEach(k => {
      const [cls, sec] = k.split('-');
      const table = document.createElement('table');
      table.className = 'spp-summary-table';
      table.style.marginTop = '10mm';

      const thead = document.createElement('thead');
      thead.innerHTML = `
        <tr>
          <th colspan="3" style="text-align:center; font-size:12pt; font-weight:bold; border: 1px solid #000; padding: 2mm 3mm;">Class :${cls}: ${sec}</th>
        </tr>
        <tr>
          <th style="width:40%; text-align:left; border: 1px solid #000; padding: 2mm 3mm; font-weight:bold;">Rolls</th>
          <th style="width:40%; text-align:left; border: 1px solid #000; padding: 2mm 3mm; font-weight:bold;">Building:Room</th>
          <th style="width:20%; text-align:left; border: 1px solid #000; padding: 2mm 3mm; font-weight:bold;">Total</th>
        </tr>
      `;
      table.appendChild(thead);

      const tbody = document.createElement('tbody');
      let classTotal = 0;
      const roomLabels = Object.keys(classMap[k]).sort();
      roomLabels.forEach(roomLabel => {
        const rolls = classMap[k][roomLabel];
        classTotal += rolls.length;
        const rollRangeStr = getRollRanges(rolls);
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td style="border: 1px solid #000; padding: 2mm 3mm;">${rollRangeStr}</td>
          <td style="border: 1px solid #000; padding: 2mm 3mm;">${roomLabel}</td>
          <td style="border: 1px solid #000; padding: 2mm 3mm;">${rolls.length} students</td>
        `;
        tbody.appendChild(tr);
      });

      const tfoot = document.createElement('tfoot');
      tfoot.innerHTML = `
        <tr>
          <td colspan="3" style="text-align:center; font-weight:bold; border: 1px solid #000; padding: 2mm 3mm;">Total ${classTotal} students</td>
        </tr>
      `;
      table.appendChild(tbody);
      table.appendChild(tfoot);
      sheet.appendChild(table);
    });

    return sheet;
  }

  function buildRoomWiseSummary(plan: Plan, rooms: PlanRoom[]): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'spp-print-sheet';
    sheet.style.pageBreakAfter = 'always';

    const title = document.createElement('h2');
    title.className = 'spp-summary-title';
    title.textContent = 'Room-wise summary';
    sheet.appendChild(title);

    rooms.forEach(pr => {
      const table = document.createElement('table');
      table.className = 'spp-summary-table';
      table.style.marginTop = '10mm';

      const thead = document.createElement('thead');
      thead.innerHTML = `
        <tr>
          <th colspan="3" style="text-align:center; font-size:12pt; font-weight:bold; border: 1px solid #000; padding: 2mm 3mm;">Building :${pr.room_info.building} Room: ${pr.room_info.room_no}</th>
        </tr>
        <tr>
          <th style="width:30%; text-align:left; border: 1px solid #000; padding: 2mm 3mm; font-weight:bold;">Class</th>
          <th style="width:50%; text-align:left; border: 1px solid #000; padding: 2mm 3mm; font-weight:bold;">Rolls</th>
          <th style="width:20%; text-align:left; border: 1px solid #000; padding: 2mm 3mm; font-weight:bold;">Total</th>
        </tr>
      `;
      table.appendChild(thead);

      const roomClassMap: Record<string, string[]> = {};
      pr.seats.forEach(seat => {
        (seat.students || []).forEach(s => {
          if (!s) return;
          const key = classKey(s.cls, s.section);
          if (!roomClassMap[key]) roomClassMap[key] = [];
          roomClassMap[key].push(s.roll);
        });
      });

      const tbody = document.createElement('tbody');
      let roomTotal = 0;
      const keys = Object.keys(roomClassMap).sort((a, b) => {
        const [ca] = a.split('-'), [cb] = b.split('-');
        return (Number(ca) || 0) - (Number(cb) || 0) || a.localeCompare(b);
      });

      keys.forEach(k => {
        const [cls, sec] = k.split('-');
        const rolls = roomClassMap[k];
        roomTotal += rolls.length;
        const rollRangeStr = getRollRanges(rolls);
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td style="border: 1px solid #000; padding: 2mm 3mm;">${cls}:${sec}</td>
          <td style="border: 1px solid #000; padding: 2mm 3mm;">${rollRangeStr}</td>
          <td style="border: 1px solid #000; padding: 2mm 3mm;">${rolls.length} students</td>
        `;
        tbody.appendChild(tr);
      });

      const tfoot = document.createElement('tfoot');
      tfoot.innerHTML = `
        <tr>
          <td colspan="3" style="text-align:center; font-weight:bold; border: 1px solid #000; padding: 2mm 3mm;">Total ${roomTotal} students</td>
        </tr>
      `;
      table.appendChild(tbody);
      table.appendChild(tfoot);
      sheet.appendChild(table);
    });

    return sheet;
  }

  function buildSeatLabels(plan: Plan, rooms: PlanRoom[]): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'spp-print-sheet';
    sheet.style.pageBreakAfter = 'always';

    const title = document.createElement('h2');
    title.className = 'spp-summary-title';
    title.textContent = 'Seat Label';
    sheet.appendChild(title);

    const container = document.createElement('div');
    container.className = 'spp-seat-labels-container';

    rooms.forEach(pr => {
      pr.seats.forEach(seat => {
        (seat.students || []).forEach(s => {
          if (!s) return;
          const card = document.createElement('div');
          card.className = 'spp-seat-label-card';
          card.innerHTML = `
            <div style="font-weight:bold; font-size:12pt; margin-bottom:2mm;">Class: ${s.cls}:${s.section}</div>
            <div style="font-weight:bold; font-size:14pt;">Roll: ${s.roll}</div>
          `;
          container.appendChild(card);
        });
      });
    });

    sheet.appendChild(container);
    return sheet;
  }

  function printPlan(): void {
    const plan = state.currentPlan;
    if (!plan) { alert('Generate a plan first'); return; }
    const format = ($('sppExpFormat') as HTMLSelectElement).value;
    const roomVal = ($('sppExpRoom') as HTMLSelectElement).value;

    let rooms: PlanRoom[];
    if (roomVal === 'all') rooms = plan.rooms;
    else rooms = [plan.rooms[+roomVal]];

    const printArea = $('sppPrintArea')!;
    printArea.innerHTML = '';

    if (format === 'summary' || format === 'all_formats') {
      printArea.appendChild(buildGateSummary(plan, rooms));
    }
    if (format === 'class_summary' || format === 'all_formats') {
      printArea.appendChild(buildClassWiseSummary(plan, rooms));
    }
    if (format === 'room_summary' || format === 'all_formats') {
      printArea.appendChild(buildRoomWiseSummary(plan, rooms));
    }

    rooms.forEach(pr => {
      if (format === 'seatmap' || format === 'both' || format === 'all_formats') {
        printArea.appendChild(buildPrintSeatMap(pr, plan));
      }
      if (format === 'attendance' || format === 'both' || format === 'all_formats') {
        printArea.appendChild(buildPrintAttendance(pr, plan));
      }
    });

    if (format === 'seat_label' || format === 'all_formats') {
      printArea.appendChild(buildSeatLabels(plan, rooms));
    }

    setTimeout(() => window.print(), 200);
  }

  function buildGateSummary(plan: Plan, rooms: PlanRoom[]): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'spp-print-sheet';
    sheet.style.pageBreakAfter = 'always';

    const classMap: Record<string, { cls: string; section: string; rooms: Set<string>; students: { name: string; roll: string; room: string; seat: string }[] }> = {};
    rooms.forEach(pr => {
      const roomLabel = `${pr.room_info.building} — Room ${pr.room_info.room_no}`;
      pr.seats.forEach(seat => {
        (seat.students || []).forEach(s => {
          if (!s) return;
          const key = `${s.cls}-${s.section}`;
          if (!classMap[key]) classMap[key] = { cls: s.cls, section: s.section, rooms: new Set(), students: [] };
          classMap[key].rooms.add(roomLabel);
          classMap[key].students.push({ name: s.name, roll: s.roll, room: roomLabel, seat: `${seat.row + 1}-${seat.col + 1}` });
        });
      });
    });

    const keys = Object.keys(classMap).sort((a, b) => {
      const ma = classMap[a], mb = classMap[b];
      const ca = Number(ma.cls) || 0, cb = Number(mb.cls) || 0;
      if (ca !== cb) return ca - cb;
      return ma.section.localeCompare(mb.section);
    });

    const totalStudents = rooms.reduce((s, r) => s + r.seats.reduce((a, seat) => a + seat.students.filter(st => st).length, 0), 0);
    sheet.innerHTML = `
      <h2>📌 Gate Summary — Exam Seat Assignment</h2>
      <div class="meta">${plan.exam} | Session: ${plan.session} | Year: ${plan.year}<br>
      Total: ${rooms.length} rooms, ${totalStudents} students, ${keys.length} class-sections</div>`;

    let summaryHTML = `<table class="spp-att-table" style="margin-top:8mm">
      <thead><tr><th>Class</th><th>Section</th><th>Students</th><th>Room(s)</th></tr></thead><tbody>`;
    keys.forEach(k => {
      const c = classMap[k];
      summaryHTML += `<tr><td style="text-align:center">${c.cls}</td><td style="text-align:center">${c.section}</td><td style="text-align:center">${c.students.length}</td><td>${[...c.rooms].join(', ')}</td></tr>`;
    });
    summaryHTML += '</tbody></table>';

    let detailHTML = '<div style="margin-top:10mm">';
    keys.forEach(k => {
      const c = classMap[k];
      c.students.sort((a, b) => (Number(a.roll) || 0) - (Number(b.roll) || 0));
      detailHTML += `<h3 style="margin:6mm 0 2mm;font-size:12pt;border-bottom:1px solid #333;padding-bottom:1mm">Class ${c.cls} — Section ${c.section} (${c.students.length} students)</h3>`;
      detailHTML += `<table class="spp-att-table"><thead><tr><th>Sl.</th><th>Roll</th><th>Name</th><th>Room</th><th>Seat</th></tr></thead><tbody>`;
      c.students.forEach((s, i) => {
        detailHTML += `<tr><td>${i + 1}</td><td>${s.roll}</td><td>${s.name}</td><td>${s.room}</td><td>${s.seat}</td></tr>`;
      });
      detailHTML += '</tbody></table>';
    });
    detailHTML += '</div>';

    sheet.innerHTML += summaryHTML + detailHTML;
    return sheet;
  }

  function buildPrintSeatMap(pr: PlanRoom, plan: Plan): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'spp-print-sheet';
    sheet.style.pageBreakAfter = 'always';

    const cols = pr.room_info.cols;
    const cr = getColRows(pr.room_info);
    const maxR = getMaxRows(pr.room_info);
    const bs = pr.room_info.bench_size || 1;
    const cap = roomCap(pr.room_info);
    const cellTypes = pr.room_info.cellTypes || {};
    const lookup: Record<string, SeatEntry> = {};
    pr.seats.forEach(s => { lookup[s.row + '-' + s.col] = s; });

    const isUni = cr.every(v => v === cr[0]);
    sheet.innerHTML = `
      <h2>📋 Exam Seat Plan</h2>
      <div class="meta">${plan.exam} | Session: ${plan.session} | Year: ${plan.year}<br>
      ${pr.room_info.building} — Room: ${pr.room_info.room_no} (${isUni ? cr[0] + ' rows' : 'rows: ' + cr.join(',')} × ${cols} cols${bs > 1 ? ' ×' + bs + '/bench' : ''} = ${cap} seats)</div>`;

    const grid = document.createElement('div');
    grid.className = 'spp-print-grid';
    grid.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;

    for (let r = 0; r < maxR; r++) {
      for (let c = 0; c < cols; c++) {
        if (r >= cr[c]) { grid.appendChild(document.createElement('div')); continue; }
        const ct = cellTypes[`${r}-${c}`] || 'desk';
        const seat = lookup[r + '-' + c];
        const cell = document.createElement('div');
        cell.className = 'spp-print-cell';

        if (ct === 'aisle') { cell.className += ' empty-cell'; cell.innerHTML = '<div style="color:#999">Aisle</div>'; grid.appendChild(cell); continue; }
        if (ct === 'broken') { cell.className += ' empty-cell'; cell.innerHTML = '<div style="color:#dc2626">Broken</div>'; grid.appendChild(cell); continue; }
        if (ct === 'instructor') { cell.innerHTML = '<div style="font-weight:700;color:#15803d">Instructor</div>'; grid.appendChild(cell); continue; }

        const studs = seat ? seat.students : [];
        const hasAny = studs.some(s => s);
        if (hasAny) {
          cell.innerHTML = studs.map(s => {
            if (!s) return '<div style="color:#ccc;font-size:7pt">— empty —</div>';
            return `<div class="pc-name">${s.name}</div><div class="pc-info">Class ${s.cls}-${s.section} | Roll: ${s.roll}</div>`;
          }).join(bs > 1 ? '<hr style="margin:1mm 0;border:none;border-top:1px dashed #999">' : '');
        } else {
          cell.className += ' empty-cell';
          cell.innerHTML = '<div style="color:#ccc">empty</div>';
        }
        grid.appendChild(cell);
      }
    }
    sheet.appendChild(grid);
    return sheet;
  }

  function buildPrintAttendance(pr: PlanRoom, plan: Plan): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'spp-print-sheet';
    sheet.style.pageBreakAfter = 'always';

    const students: (Student & { seatLabel: string })[] = [];
    pr.seats.forEach(s => {
      (s.students || []).forEach(st => {
        if (st) students.push({ ...st, seatLabel: `${s.row + 1}-${s.col + 1}` });
      });
    });
    students.sort((a, b) => {
      const ca = (Number(a.cls) || 0), cb = (Number(b.cls) || 0);
      if (ca !== cb) return ca - cb;
      if (a.section !== b.section) return a.section.localeCompare(b.section);
      return (Number(a.roll) || 0) - (Number(b.roll) || 0);
    });

    sheet.innerHTML = `
      <h2>📝 Attendance Sheet</h2>
      <div class="meta">${plan.exam} | Session: ${plan.session} | Year: ${plan.year}<br>
      ${pr.room_info.building} — Room: ${pr.room_info.room_no} | Total: ${students.length} students</div>`;

    let html = `<table class="spp-att-table">
      <thead><tr><th>Sl.</th><th>Name</th><th>Class</th><th>Section</th><th>Roll</th><th>Seat</th><th>Signature</th></tr></thead><tbody>`;
    students.forEach((s, i) => {
      html += `<tr><td>${i + 1}</td><td>${s.name}</td><td>${s.cls}</td><td>${s.section}</td><td>${s.roll}</td><td>${s.seatLabel}</td><td style="min-width:25mm"></td></tr>`;
    });
    html += '</tbody></table>';
    sheet.innerHTML += html;
    return sheet;
  }

  /* ─────────────────── DB STUDENT COUNT ─────────────────── */
  async function loadStudentCount(): Promise<void> {
    try {
      const { count, error } = await sb.from('student_database').select('iid', { count: 'exact', head: true }).not('active_roll', 'is', null);
      if (!error && count !== null) {
        const el = $('spp-ds-students');
        if (el) el.textContent = String(count);
      }
    } catch { /* silent */ }
  }

  /* ─────────────────── FIXED ASSIGNMENT ─────────────────── */
  function refreshFixedTab(): void {
    const fxSess = $('sppFxSession') as HTMLSelectElement;
    const plSess = $('sppPlSession') as HTMLSelectElement;
    if (fxSess && plSess) fxSess.innerHTML = plSess.innerHTML;
    refreshFixedRoomSelect();
    renderFixedRules();
  }

  function refreshFixedRoomSelect(): void {
    const sel = $('sppFxRoom') as HTMLSelectElement;
    sel.innerHTML = '<option value="">— select —</option>';
    state.rooms.forEach(rm => {
      const o = document.createElement('option');
      o.value = rm.id;
      o.textContent = `${rm.building} — ${rm.room_no} (${roomCap(rm)} seats)`;
      sel.appendChild(o);
    });
  }

  function updateFixedClassList(): void {
    const sess = ($('sppFxSession') as HTMLSelectElement).value;
    const sel = $('sppFxClass') as HTMLSelectElement;
    sel.innerHTML = '<option value="">— select —</option>';
    if (!sess) return;
    const classes = state.classMap.get(sess);
    if (!classes) return;
    [...classes].sort((a, b) => {
      const [ca] = a.split('-'), [cb] = b.split('-');
      return (Number(ca) || 0) - (Number(cb) || 0) || a.localeCompare(b);
    }).forEach(key => {
      const o = document.createElement('option');
      o.value = key; o.textContent = `Class ${key}`;
      sel.appendChild(o);
    });
  }

  function addFixedRule(): void {
    const classSection = ($('sppFxClass') as HTMLSelectElement).value;
    const rollRange = ($('sppFxRolls') as HTMLInputElement).value.trim() || 'all';
    const roomId = ($('sppFxRoom') as HTMLSelectElement).value;
    const startRow = Math.max(1, parseInt(($('sppFxStartRow') as HTMLInputElement).value) || 1);
    const startCol = Math.max(1, parseInt(($('sppFxStartCol') as HTMLInputElement).value) || 1);
    if (!classSection) { alert('Select a class-section'); return; }
    if (!roomId) { alert('Select a target room'); return; }
    const room = state.rooms.find(r => r.id === roomId);
    fixedRules.push({ id: uid(), classSection, rollRange, roomId, roomLabel: room ? `${room.building} — ${room.room_no}` : roomId, startRow, startCol });
    saveFixedRules();
    renderFixedRules();
    setStatus('Rule added');
  }

  function deleteFixedRule(id: string): void {
    fixedRules = fixedRules.filter(r => r.id !== id);
    saveFixedRules();
    renderFixedRules();
  }

  function renderFixedRules(): void {
    const wrap = $('sppRulesList')!;
    const countEl = $('sppRuleCount');
    if (countEl) countEl.textContent = fixedRules.length ? `(${fixedRules.length})` : '';
    if (!fixedRules.length) { wrap.innerHTML = '<span class="spp-text-muted">No rules added yet.</span>'; return; }
    wrap.innerHTML = '';
    fixedRules.forEach((rule, i) => {
      const div = document.createElement('div');
      div.className = 'spp-flex-between';
      div.style.cssText = 'padding:.4rem 0; border-bottom:1px solid var(--spp-border); font-size:.85rem';
      div.innerHTML = `
        <div>
          <strong>${i + 1}.</strong> Class ${rule.classSection}
          (rolls: ${rule.rollRange}) → ${rule.roomLabel}
          <span class="spp-text-muted">from row ${rule.startRow}, col ${rule.startCol}</span>
        </div>
        <button class="spp-btn spp-btn-danger spp-btn-sm spp-del-rule-btn" data-id="${rule.id}">✕</button>`;
      wrap.appendChild(div);
    });
    wrap.querySelectorAll('.spp-del-rule-btn').forEach(btn => {
      btn.addEventListener('click', () => deleteFixedRule((btn as HTMLElement).dataset.id!));
    });
  }

  function parseRollRange(rangeStr: string): { min: number; max: number } | null {
    const s = rangeStr.trim().toLowerCase();
    if (s === 'all' || s === '') return null;
    const m = s.match(/^(\d+)\s*[-–]\s*(\d+)$/);
    if (m) return { min: parseInt(m[1]), max: parseInt(m[2]) };
    const n = parseInt(s);
    if (!isNaN(n)) return { min: n, max: n };
    return null;
  }

  function matchesRollRange(student: Student, range: { min: number; max: number } | null): boolean {
    if (!range) return true;
    const roll = parseInt(student.roll);
    if (isNaN(roll)) return false;
    return roll >= range.min && roll <= range.max;
  }

  async function generateFixedPlan(): Promise<void> {
    const sess = ($('sppFxSession') as HTMLSelectElement).value;
    const year = ($('sppFxYear') as HTMLSelectElement).value;
    const exam = ($('sppFxExam') as HTMLSelectElement).value;
    const pattern = (document.querySelector('input[name=sppFxPattern]:checked') as HTMLInputElement)?.value || 'Z';

    if (!sess) { alert('Select a session'); return; }
    if (!fixedRules.length) { alert('Add at least one rule'); return; }

    const neededClasses = new Set(fixedRules.map(r => r.classSection));
    setStatus('Loading students for fixed plan…');

    try {
      const { data, error } = await sb.from('student_database')
        .select('iid, student_name_en, student_name_bn, father_name_en, group, status, session, active_class, active_section, active_roll, student_gender')
        .eq('session', sess)
        .not('active_roll', 'is', null);
      if (error) throw error;

      const allStudents: Student[] = [];
      for (const r of (data || [])) {
        if (String(r.status || '').toUpperCase().includes('TC')) continue;
        const cls = String(r.active_class || '').trim();
        const sec = String(r.active_section || '').trim();
        if (!neededClasses.has(classKey(cls, sec))) continue;
        allStudents.push({
          iid: r.iid || '', name: String(r.student_name_en || r.student_name_bn || ''),
          nameBn: String(r.student_name_bn || ''), father: String(r.father_name_en || ''),
          cls, section: sec, roll: String(r.active_roll || ''),
          group: String(r.group || ''), gender: genderLabel(String(r.student_gender || ''))
        });
      }

      if (!allStudents.length) { setStatus('No students found for the selected classes'); return; }

      const plan: Plan = {
        id: uid(), exam, year, session: sess,
        pattern, antiCheat: false, genderSep: false,
        created: new Date().toISOString(),
        rooms: [], unassigned: []
      };

      const roomSeatsMap: Record<string, SeatEntry[]> = {};
      const usedRoomIds = [...new Set(fixedRules.map(r => r.roomId))];
      const selRooms = state.rooms.filter(r => usedRoomIds.includes(r.id));

      for (const room of selRooms) {
        const bs = room.bench_size || 1;
        const cr = getColRows(room);
        const maxR = getMaxRows(room);
        const cellTypes = room.cellTypes || {};
        const seatOrder: [number, number][] = [];

        if (pattern === 'Z') {
          for (let r = 0; r < maxR; r++)
            for (let c = 0; c < room.cols; c++)
              if (r < cr[c]) seatOrder.push([r, c]);
        } else {
          for (let c = 0; c < room.cols; c++)
            for (let r = 0; r < cr[c]; r++)
              seatOrder.push([r, c]);
        }

        const seats: SeatEntry[] = [];
        for (const [r, c] of seatOrder) {
          seats.push({ row: r, col: c, students: Array(bs).fill(null) });
        }
        roomSeatsMap[room.id] = seats;
      }

      const placed = new Set<string>();

      // --- Stage 1: Apply Fixed Rules first ---
      for (const rule of fixedRules) {
        const room = selRooms.find(r => r.id === rule.roomId);
        if (!room) continue;
        const rollRange = parseRollRange(rule.rollRange);
        const matching = allStudents.filter(s => {
          if (placed.has(s.iid)) return false;
          if (classKey(s.cls, s.section) !== rule.classSection) return false;
          return matchesRollRange(s, rollRange);
        }).sort((a, b) => (Number(a.roll) || 0) - (Number(b.roll) || 0));

        if (!matching.length) continue;
        const seats = roomSeatsMap[rule.roomId];
        if (!seats) continue;

        const bs = room.bench_size || 1;
        const cr = getColRows(room);
        const maxR = getMaxRows(room);
        const cellTypes = room.cellTypes || {};
        const startR = rule.startRow - 1;
        const startC = rule.startCol - 1;

        const fullOrder: [number, number][] = [];
        if (pattern === 'Z') {
          for (let r = 0; r < maxR; r++)
            for (let c = 0; c < room.cols; c++)
              if (r < cr[c]) fullOrder.push([r, c]);
        } else {
          for (let c = 0; c < room.cols; c++)
            for (let r = 0; r < cr[c]; r++)
              fullOrder.push([r, c]);
        }

        // Filter out non-desk cells
        const deskOrder = fullOrder.filter(([r, c]) => (cellTypes[`${r}-${c}`] || 'desk') === 'desk');

        const startIdx = deskOrder.findIndex(([r, c]) => {
          if (pattern === 'Z') return r > startR || (r === startR && c >= startC);
          return c > startC || (c === startC && r >= startR);
        });
        const seatOrder = startIdx >= 0 ? deskOrder.slice(startIdx) : deskOrder;

        let sPtr = 0;
        for (const [r, c] of seatOrder) {
          if (sPtr >= matching.length) break;
          const seat = seats.find(s => s.row === r && s.col === c);
          if (!seat) continue;
          for (let b = 0; b < bs; b++) {
            if (sPtr >= matching.length) break;
            if (seat.students[b] === null) {
              seat.students[b] = matching[sPtr];
              placed.add(matching[sPtr].iid);
              sPtr++;
            }
          }
        }
      }

      // --- Stage 2: Distribute remaining students into remaining seats ---
      const unassignedStudents = allStudents.filter(s => !placed.has(s.iid));
      let ptr = 0;

      for (const room of selRooms) {
        const seats = roomSeatsMap[room.id];
        if (!seats) continue;
        const bs = room.bench_size || 1;
        const cr = getColRows(room);
        const maxR = getMaxRows(room);
        const cellTypes = room.cellTypes || {};

        const fullOrder: [number, number][] = [];
        if (pattern === 'Z') {
          for (let r = 0; r < maxR; r++)
            for (let c = 0; c < room.cols; c++)
              if (r < cr[c]) fullOrder.push([r, c]);
        } else {
          for (let c = 0; c < room.cols; c++)
            for (let r = 0; r < cr[c]; r++)
              fullOrder.push([r, c]);
        }

        const deskOrder = fullOrder.filter(([r, c]) => (cellTypes[`${r}-${c}`] || 'desk') === 'desk');

        for (const [r, c] of deskOrder) {
          if (ptr >= unassignedStudents.length) break;
          const seat = seats.find(s => s.row === r && s.col === c);
          if (!seat) continue;
          for (let b = 0; b < bs; b++) {
            if (ptr >= unassignedStudents.length) break;
            if (seat.students[b] === null) {
              seat.students[b] = unassignedStudents[ptr++];
            }
          }
        }
      }

      for (const room of selRooms) {
        plan.rooms.push({ room_id: room.id, room_info: { ...room }, seats: roomSeatsMap[room.id] });
      }

      plan.unassigned = unassignedStudents.slice(ptr);

      state.currentPlan = plan;
      state.students = allStudents;
      activeGridRoom = 0;

      $('sppFixedResultInfo')!.innerHTML = `<div class="spp-badge spp-badge-green" style="font-size:.9rem;padding:.5rem 1rem">✓ Fixed plan generated: ${placed.size + ptr} seated, ${plan.unassigned.length} unassigned. Switching to Seat Plan tab…</div>`;

      setTimeout(() => {
        (document.querySelector('.spp-tab-btn[data-tab=planner]') as HTMLElement)?.click();
        ($('sppSavePlanBtn') as HTMLButtonElement).disabled = false;
        renderGrid();
      }, 500);

      setStatus(`Fixed plan: ${placed.size + ptr} seated, ${plan.unassigned.length} remaining`);

    } catch (e: any) {
      console.error(e);
      setStatus('Fixed plan failed: ' + e.message);
    }
  }

  /* ─────────────────── EVENT BINDINGS ─────────────────── */
  $('sppAddRoomBtn')!.addEventListener('click', addRoom);
  $('sppCancelEditBtn')!.addEventListener('click', cancelEdit);
  $('sppClearRoomsBtn')!.addEventListener('click', () => { if (!confirm('Delete all rooms?')) return; state.rooms = []; saveRooms(); renderRoomCards(); });
  $('sppPlSession')!.addEventListener('change', updateClassList);
  $('sppClassList')!.addEventListener('change', updateSelClassCount);
  $('sppToggleAllClasses')!.addEventListener('click', () => {
    const cbs = document.querySelectorAll('.spp-cls-check') as NodeListOf<HTMLInputElement>;
    const allChecked = [...cbs].every(cb => cb.checked);
    cbs.forEach(cb => cb.checked = !allChecked);
    updateSelClassCount();
  });
  $('sppGenerateBtn')!.addEventListener('click', async () => {
    await loadStudentsForPlan();
    if (!state.students.length) { setStatus('No students found'); return; }
    generatePlan();
  });
  $('sppSavePlanBtn')!.addEventListener('click', savePlan);
  $('sppClearPlanBtn')!.addEventListener('click', () => {
    state.currentPlan = null;
    ($('sppSavePlanBtn') as HTMLButtonElement).disabled = true;
    renderGrid();
    $('sppGridRoomTabs')!.innerHTML = '';
    $('sppGridLegend')!.innerHTML = '';
    $('sppOverflowMsg')!.innerHTML = '';
    setStatus('Plan cleared');
  });
  $('sppSeatSearch')!.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') searchStudent(); });
  $('sppPrintBtn')!.addEventListener('click', printPlan);
  $('sppBackBtn')!.addEventListener('click', (e) => {
    e.preventDefault();
    navigateTo('/');
  });

  // Fixed tab
  $('sppFxSession')!.addEventListener('change', updateFixedClassList);
  $('sppAddRuleBtn')!.addEventListener('click', addFixedRule);
  $('sppClearRulesBtn')!.addEventListener('click', () => { fixedRules = []; saveFixedRules(); renderFixedRules(); });

  /* ─────────────────── BOOT ─────────────────── */
  loadRooms();
  loadSavedPlans();
  loadFixedRules();
  refreshDashboard();
  renderRoomCards();
  loadSessionFilter();
  loadStudentCount();
}
